import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent } from "react";
import { useNavigate } from "react-router-dom";

import {
  addCommunitySocialComment,
  createCommunitySocialPost,
  fetchCommunitySocialPosts,
  toggleCommunitySocialLike,
  uploadCommunityImage,
  type PublicAuthUser,
  type PublicSocialPostItem
} from "@/features/public/api";
import {
  hasMemberAccessToken,
  readStoredMemberUser,
  restoreMemberAccessToken,
  subscribeToMemberSessionChanges,
  withMemberSession as withStoredMemberSession
} from "@/features/public/memberSession";
import { connectCommunityRealtime } from "@/shared/realtime/communityRealtime";

function formatDateTime(value: string) {
  return new Date(value).toLocaleString("fr-FR", {
    dateStyle: "medium",
    timeStyle: "short"
  });
}

function buildInitials(name: string) {
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((chunk) => chunk[0]?.toUpperCase() ?? "")
    .join("");
}

function AuthorAvatar({ name, avatar, size = "w-11 h-11" }: { name: string; avatar: string | null; size?: string }) {
  if (avatar) {
    return <img src={avatar} alt={name} className={`${size} rounded-full object-cover object-[center_24%] flex-shrink-0`} />;
  }

  return (
    <div className={`${size} rounded-full bg-[#E8521A] flex items-center justify-center text-white text-xs font-bold flex-shrink-0`}>
      {buildInitials(name)}
    </div>
  );
}

function PostCard({
  post,
  isLoggedIn,
  currentUserLabel,
  liking,
  commenting,
  onRequireLogin,
  onToggleLike,
  onComment
}: {
  post: PublicSocialPostItem;
  isLoggedIn: boolean;
  currentUserLabel: string;
  liking: boolean;
  commenting: boolean;
  onRequireLogin: () => void;
  onToggleLike: (postId: string) => void;
  onComment: (postId: string, content: string) => void;
}) {
  const [showComments, setShowComments] = useState(false);
  const [newComment, setNewComment] = useState("");

  const typeColors: Record<string, string> = {
    post: "bg-gray-100 text-gray-600",
    challenge: "bg-orange-100 text-orange-700",
    achievement: "bg-amber-100 text-amber-700"
  };

  const typeLabels: Record<string, string> = {
    post: "Publication",
    challenge: "Défi",
    achievement: "Réussite"
  };

  const handleCommentSubmit = () => {
    const content = newComment.trim();

    if (!content) {
      return;
    }

    if (!isLoggedIn) {
      onRequireLogin();
      return;
    }

    onComment(post.id, content);
    setNewComment("");
    setShowComments(true);
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
      <div className="flex items-start gap-3 p-5 pb-3">
        <AuthorAvatar name={post.author.name} avatar={post.author.avatar} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-gray-900 text-sm">{post.author.name}</span>
            <span className={`text-xs px-2 py-0.5 rounded-full ${typeColors[post.type] ?? typeColors.post}`}>
              {typeLabels[post.type] ?? "Publication"}
            </span>
          </div>
          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
            <span className="text-xs text-gray-400">{post.author.squad}</span>
            <span className="text-gray-300">·</span>
            <span className="text-xs text-gray-400">{formatDateTime(post.timestamp)}</span>
          </div>
        </div>
        <div className="rounded-lg bg-gray-50 px-2.5 py-1 text-[11px] font-medium text-gray-500">
          {post.author.role}
        </div>
      </div>

      <div className="px-5 pb-3">
        <p className="text-gray-700 text-sm leading-relaxed whitespace-pre-line">{post.content}</p>
        {post.tags?.length ? (
          <div className="flex flex-wrap gap-1.5 mt-2">
            {post.tags.map((tag) => (
              <span key={tag} className="text-xs text-[#E8521A] font-medium">
                #{tag}
              </span>
            ))}
          </div>
        ) : null}
      </div>

      {post.image ? (
        <div className="w-full h-64 overflow-hidden">
          <img src={post.image} alt={post.author.name} className="w-full h-full object-cover" />
        </div>
      ) : null}

      <div className="flex items-center gap-1 px-5 py-3 border-t border-gray-50 flex-wrap">
        <button
          onClick={() => {
            if (!isLoggedIn) {
              onRequireLogin();
              return;
            }

            onToggleLike(post.id);
          }}
          disabled={liking}
          className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium transition-all ${
            post.isLiked ? "text-rose-500 bg-rose-50" : "text-gray-500 hover:bg-gray-100"
          } ${liking ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
        >
          <i className={`${post.isLiked ? "ri-heart-fill" : "ri-heart-line"} text-base`}></i>
          <span>{post.likes}</span>
        </button>
        <button
          onClick={() => setShowComments((current) => !current)}
          className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium text-gray-500 hover:bg-gray-100 transition-colors cursor-pointer"
        >
          <i className="ri-chat-3-line text-base"></i>
          <span>{post.comments.length}</span>
        </button>
        {!isLoggedIn ? (
          <button
            onClick={onRequireLogin}
            className="ml-auto rounded-xl border border-orange-100 bg-orange-50 px-3 py-2 text-sm font-medium text-[#E8521A] transition-colors hover:bg-orange-100"
          >
            Se connecter pour interagir
          </button>
        ) : null}
      </div>

      {showComments ? (
        <div className="px-5 pb-4 border-t border-gray-50 pt-3 space-y-3">
          {post.comments.length > 0 ? (
            post.comments.map((comment) => (
              <div key={comment.id} className="flex gap-2.5">
                <AuthorAvatar name={comment.author.name} avatar={comment.author.avatar} size="w-8 h-8" />
                <div className="flex-1 bg-gray-50 rounded-xl px-3 py-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-xs text-gray-900">{comment.author.name}</span>
                    <span className="text-gray-400 text-xs">{formatDateTime(comment.timestamp)}</span>
                  </div>
                  <p className="text-sm text-gray-700 mt-0.5">{comment.content}</p>
                </div>
              </div>
            ))
          ) : (
            <div className="rounded-xl border border-dashed border-gray-200 px-4 py-5 text-center text-sm text-gray-400">
              Aucun commentaire pour le moment.
            </div>
          )}

          <div className="flex gap-2 mt-3">
            <div className="w-8 h-8 rounded-full bg-[#E8521A] flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
              {currentUserLabel}
            </div>
            <div className="flex-1 flex gap-2">
              <input
                type="text"
                value={newComment}
                disabled={!isLoggedIn}
                onChange={(event) => setNewComment(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    handleCommentSubmit();
                  }
                }}
                placeholder={isLoggedIn ? "Écrire un commentaire..." : "Connectez-vous pour commenter"}
                className="flex-1 px-3 py-2 bg-gray-100 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-200 disabled:cursor-not-allowed"
              />
              <button
                onClick={handleCommentSubmit}
                disabled={commenting || !newComment.trim()}
                className="px-3 py-2 bg-[#E8521A] text-white rounded-xl text-sm hover:bg-[#d44a16] disabled:opacity-60 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
              >
                <i className="ri-send-plane-fill"></i>
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function NewPostModal({
  currentUser,
  isSubmitting,
  onClose,
  onSubmit
}: {
  currentUser: PublicAuthUser | null;
  isSubmitting: boolean;
  onClose: () => void;
  onSubmit: (payload: { content: string; imageFile: File | null }) => void;
}) {
  const [content, setContent] = useState("");
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!imageFile) {
      setImagePreview(null);
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => setImagePreview((event.target?.result as string | null) ?? null);
    reader.readAsDataURL(imageFile);
  }, [imageFile]);

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    event.target.value = "";
    setImageFile(file);
  };

  const handleSubmit = () => {
    const trimmedContent = content.trim();

    if (!trimmedContent) {
      return;
    }

    onSubmit({
      content: trimmedContent,
      imageFile
    });
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg" onClick={(event) => event.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <h3 className="font-bold text-gray-900">Nouvelle publication</h3>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-500 cursor-pointer">
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>
        <div className="p-5">
          <div className="flex gap-3 mb-4">
            <AuthorAvatar
              name={currentUser ? `${currentUser.prenom} ${currentUser.nom}` : "Membre"}
              avatar={currentUser?.avatar ?? null}
              size="w-10 h-10"
            />
            <textarea
              value={content}
              onChange={(event) => setContent(event.target.value)}
              placeholder="Quoi de neuf ? Partagez avec la communauté..."
              rows={4}
              className="flex-1 resize-none text-sm text-gray-700 focus:outline-none placeholder-gray-400"
            />
          </div>

          {imagePreview ? (
            <div className="relative mb-4 rounded-xl overflow-hidden">
              <img src={imagePreview} alt="Aperçu de publication" className="w-full h-48 object-cover" />
              <button
                onClick={() => setImageFile(null)}
                className="absolute top-2 right-2 w-7 h-7 bg-black/50 text-white rounded-full flex items-center justify-center cursor-pointer"
              >
                <i className="ri-close-line text-sm"></i>
              </button>
            </div>
          ) : null}

          <div className="flex items-center gap-3 pt-3 border-t border-gray-100">
            <button
              onClick={() => fileRef.current?.click()}
              className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm text-gray-600 hover:bg-gray-100 transition-colors cursor-pointer"
            >
              <i className="ri-image-line text-[#E8521A]"></i>
              Photo
            </button>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFileChange} />
            <button
              onClick={handleSubmit}
              disabled={isSubmitting || !content.trim()}
              className="ml-auto px-5 py-2 bg-[#E8521A] text-white rounded-xl text-sm font-semibold hover:bg-[#d44a16] transition-colors disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
            >
              {isSubmitting ? "Publication..." : "Publier"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function SocialFeed() {
  const navigate = useNavigate();
  const [posts, setPosts] = useState<PublicSocialPostItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showNewPost, setShowNewPost] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(hasMemberAccessToken());
  const [currentUser, setCurrentUser] = useState<PublicAuthUser | null>(() => readStoredMemberUser());
  const [creatingPost, setCreatingPost] = useState(false);
  const [likingPostId, setLikingPostId] = useState<string | null>(null);
  const [commentingPostId, setCommentingPostId] = useState<string | null>(null);

  const currentUserLabel = useMemo(() => {
    if (!currentUser) {
      return "?";
    }

    return buildInitials(`${currentUser.prenom} ${currentUser.nom}`) || "M";
  }, [currentUser]);

  const syncSessionState = useCallback(() => {
    setIsLoggedIn(hasMemberAccessToken());
    setCurrentUser(readStoredMemberUser());
  }, []);

  const requireLogin = useCallback(() => {
    navigate("/formulaires?tab=login&redirect=%2Fsocial%3Ftab%3Dfeed");
  }, [navigate]);

  const replacePost = useCallback((updatedPost: PublicSocialPostItem) => {
    setPosts((current) => current.map((post) => (post.id === updatedPost.id ? updatedPost : post)));
  }, []);

  const upsertPost = useCallback((updatedPost: PublicSocialPostItem) => {
    setPosts((current) => {
      const hasPost = current.some((post) => post.id === updatedPost.id);

      if (!hasPost) {
        return [updatedPost, ...current];
      }

      return current.map((post) => (post.id === updatedPost.id ? updatedPost : post));
    });
  }, []);

  const loadPosts = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const accessToken = await restoreMemberAccessToken();
      const payload = await fetchCommunitySocialPosts({
        accessToken: accessToken ?? undefined
      });

      setPosts(payload);
      syncSessionState();
    } catch (caughtError) {
      setError(caughtError instanceof Error ? caughtError.message : "Impossible de charger le fil d'actualité");
    } finally {
      setLoading(false);
    }
  }, [syncSessionState]);

  useEffect(() => {
    void loadPosts();
  }, [loadPosts]);

  useEffect(() => {
    let isCancelled = false;
    let socket: Awaited<ReturnType<typeof connectCommunityRealtime>> = null;

    void (async () => {
      socket = await connectCommunityRealtime();

      if (!socket || isCancelled) {
        socket?.disconnect();
        return;
      }

      socket.on("community:social-post-updated", (updatedPost: PublicSocialPostItem) => {
        upsertPost(updatedPost);
      });
    })();

    return () => {
      isCancelled = true;
      socket?.disconnect();
    };
  }, [upsertPost]);

  useEffect(() => {
    window.addEventListener("storage", syncSessionState);
    const unsubscribe = subscribeToMemberSessionChanges(syncSessionState);

    return () => {
      window.removeEventListener("storage", syncSessionState);
      unsubscribe();
    };
  }, [syncSessionState]);

  const handleCreatePost = async (payload: { content: string; imageFile: File | null }) => {
    setCreatingPost(true);
    setError(null);

    try {
      const createdPost = await withStoredMemberSession(async (token) => {
        let imageUrl: string | undefined;

        if (payload.imageFile) {
          const uploaded = await uploadCommunityImage(token, payload.imageFile);
          imageUrl = uploaded.url;
        }

        return createCommunitySocialPost(token, {
          content: payload.content,
          type: "post",
          ...(imageUrl ? { image: imageUrl } : {})
        });
      });

      syncSessionState();

      if (!createdPost) {
        requireLogin();
        return;
      }

      setPosts((current) => [createdPost, ...current]);
      setShowNewPost(false);
    } catch (caughtError) {
      setError(caughtError instanceof Error ? caughtError.message : "Impossible de publier pour le moment");
    } finally {
      setCreatingPost(false);
    }
  };

  const handleToggleLike = async (postId: string) => {
    setLikingPostId(postId);
    setError(null);

    try {
      const updatedPost = await withStoredMemberSession((token) => toggleCommunitySocialLike(token, postId));
      syncSessionState();

      if (!updatedPost) {
        requireLogin();
        return;
      }

      replacePost(updatedPost);
    } catch (caughtError) {
      setError(caughtError instanceof Error ? caughtError.message : "Impossible de mettre à jour le like");
    } finally {
      setLikingPostId(null);
    }
  };

  const handleAddComment = async (postId: string, content: string) => {
    setCommentingPostId(postId);
    setError(null);

    try {
      const updatedPost = await withStoredMemberSession((token) => addCommunitySocialComment(token, postId, content));
      syncSessionState();

      if (!updatedPost) {
        requireLogin();
        return;
      }

      replacePost(updatedPost);
    } catch (caughtError) {
      setError(caughtError instanceof Error ? caughtError.message : "Impossible d'ajouter le commentaire");
    } finally {
      setCommentingPostId(null);
    }
  };

  return (
    <div className="space-y-4">
      {error ? (
        <div className="rounded-2xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-600">
          {error}
        </div>
      ) : null}

      <div className="bg-white rounded-2xl border border-gray-100 p-4">
        <div className="flex gap-3 items-center">
          <AuthorAvatar
            name={currentUser ? `${currentUser.prenom} ${currentUser.nom}` : "Membre"}
            avatar={currentUser?.avatar ?? null}
            size="w-10 h-10"
          />
          <button
            onClick={() => {
              if (!isLoggedIn) {
                requireLogin();
                return;
              }

              setShowNewPost(true);
            }}
            className="flex-1 text-left px-4 py-2.5 bg-gray-100 rounded-xl text-sm text-gray-400 hover:bg-gray-200 transition-colors cursor-pointer"
          >
            {isLoggedIn ? "Quoi de neuf ? Partagez avec la communauté..." : "Connectez-vous pour publier dans le réseau social"}
          </button>
        </div>
        <div className="flex gap-2 mt-3 pt-3 border-t border-gray-100">
          <button
            onClick={() => {
              if (!isLoggedIn) {
                requireLogin();
                return;
              }

              setShowNewPost(true);
            }}
            className="flex-1 flex items-center justify-center gap-2 py-2 rounded-xl text-sm text-gray-600 hover:bg-gray-100 transition-colors cursor-pointer"
          >
            <i className="ri-image-line text-[#E8521A]"></i>
            Photo
          </button>
          <button
            onClick={() => navigate("/social?tab=challenges")}
            className="flex-1 flex items-center justify-center gap-2 py-2 rounded-xl text-sm text-gray-600 hover:bg-gray-100 transition-colors cursor-pointer"
          >
            <i className="ri-trophy-line text-amber-500"></i>
            Défis
          </button>
          <button
            onClick={() => void loadPosts()}
            className="flex-1 flex items-center justify-center gap-2 py-2 rounded-xl text-sm text-gray-600 hover:bg-gray-100 transition-colors cursor-pointer"
          >
            <i className="ri-refresh-line text-teal-600"></i>
            Actualiser
          </button>
        </div>
      </div>

      {loading ? (
        Array.from({ length: 3 }).map((_, index) => (
          <div key={index} className="h-72 rounded-2xl border border-gray-100 bg-white animate-pulse"></div>
        ))
      ) : posts.length > 0 ? (
        posts.map((post) => (
          <PostCard
            key={post.id}
            post={post}
            isLoggedIn={isLoggedIn}
            currentUserLabel={currentUserLabel}
            liking={likingPostId === post.id}
            commenting={commentingPostId === post.id}
            onRequireLogin={requireLogin}
            onToggleLike={handleToggleLike}
            onComment={handleAddComment}
          />
        ))
      ) : (
        <div className="rounded-2xl border border-dashed border-gray-200 bg-white px-6 py-10 text-center">
          <i className="ri-global-line text-4xl text-gray-300"></i>
          <h3 className="mt-4 text-lg font-semibold text-gray-900">Aucune publication pour le moment</h3>
          <p className="mt-2 text-sm text-gray-500">
            Les vraies publications des membres apparaîtront ici automatiquement.
          </p>
        </div>
      )}

      {showNewPost ? (
        <NewPostModal
          currentUser={currentUser}
          isSubmitting={creatingPost}
          onClose={() => setShowNewPost(false)}
          onSubmit={(payload) => {
            void handleCreatePost(payload);
          }}
        />
      ) : null}
    </div>
  );
}
